import React, { useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ModuleGuideTour, { type ModuleGuideTourHandle } from '@/components/guide/ModuleGuideTour';
import { useTranslation } from '@/store/languageStore';
import {
  ChevronLeftIcon, 
  ChevronRightIcon, 
  TrashIcon,
  PowerIcon,
  DevicePhoneMobileIcon,
  KeyIcon,
  BellIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import FleetColumnSettingsModal from '@/components/fleet/FleetColumnSettingsModal';
import FleetOverviewHeader from '@/components/fleet/FleetOverviewHeader';
import { useFleetColumnPreferences } from '@/hooks/useFleetColumnPreferences';
import { applyFleetColumnOrder } from '@/utils/applyFleetColumnOrder';
import type { FleetOverviewColumnPreferences } from '@/types/fleetColumnPreferences';
import toast from 'react-hot-toast';
import VehicleCard from '@/components/vehicle/VehicleCard';
import AddVehicleModal from '@/components/vehicle/AddVehicleModal';
import CarInfoFilterModal from '@/components/vehicle/CarInfoFilterModal';
import VehicleDeviceLinkModal from '@/components/vehicle/VehicleDeviceLinkModal';
import FleetVehicleReferenceInput from '@/components/vehicle/FleetVehicleReferenceInput';
import { DataTable, type DataTableColumn } from '@/components/common/DataTable';
import { useFleetOverview, type FleetVehicleCardSelection } from '@/hooks/useFleetOverview';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/store/authStore';
import type { Vehicle } from '@/services/vehicles/vehiclesService';
import {
  getVehicleName,
  formatLastSeen,
  getLastSeenColor,
  getMilStatus,
  getMilColor,
  getMilTitle,
  formatFuelDisplay,
  formatEvRange,
  formatServiceDistance,
  formatCurrentOdometerKm,
} from '@/utils/fleetHelpers';
import type { FuelStatus } from '@/types/fleet';
import { formatFuelTypeLabel } from '@/utils/fuelTypeHelpers';
import {
  downloadVehicleRegisterExport,
} from '@/services/vehicles/vehicleRegisterExportService';
import {
  downloadFleetGridReportExport,
  fetchAllFilteredFleetVehicles,
} from '@/utils/fleetGridExport';
import vehiclesService from '@/services/vehicles/vehiclesService';
import { ownershipLabelKey, resolveOwnershipType } from '@/utils/vehicleOwnership';

const fleetColumns = (
  t: (key: string) => string,
  fuelStatus: Record<string, FuelStatus>,
  setSelectedVehicle: (sel: FleetVehicleCardSelection) => void,
  handleDeactivate: (v: Vehicle) => void,
  handleActivate: (v: Vehicle) => void,
  handleConnectDevice: (v: Vehicle) => void,
  handleConnectKey: (v: Vehicle) => void,
  handleSubscribe: (v: Vehicle) => void,
  handleSaveVehicleReference: (v: Vehicle, raw: string) => Promise<void>,
  referenceNames: Record<string, string[]>,
  rememberReference: (organizationId: string, name: string) => void
): DataTableColumn<Vehicle & { organization_name?: string; last_seen?: string }>[] => [
  {
    key: 'registration_number',
    label: t('fleet.vehicle'),
    sortable: true,
    sortKey: 'registration_number',
    filterable: true,
    render: (_, row) => {
      const ownership = resolveOwnershipType(row.ownership_type ?? null);
      return (
        <div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedVehicle({
                registrationNumber: row.registration_number,
                vehicleId: parseInt(String(row.id), 10),
              });
            }}
            className="text-sm font-medium text-primary hover:text-primary/80 hover:underline cursor-pointer text-left"
          >
            {getVehicleName(row)}
          </button>
          {(row.make || row.model) && (
            <div className="text-xs text-textTertiary">
              {[row.make, row.model, row.year].filter(Boolean).join(' ')}
            </div>
          )}
          {ownership ? (
            <div className="text-xs text-textSecondary">{t(ownershipLabelKey(ownership))}</div>
          ) : null}
        </div>
      );
    },
    csvValue: (_, row) => {
      const name = getVehicleName(row);
      const meta = [row.make, row.model, row.year].filter(Boolean).join(' ');
      return meta ? `${name} (${meta})` : name;
    },
  },
  {
    key: 'seats',
    label: t('fleet.sitplatser'),
    sortable: true,
    sortKey: 'seats',
    filterable: true,
    render: (v) => (v != null && v !== '' ? String(v) : <span className="text-textTertiary">-</span>),
  },
  {
    key: 'fuel_type',
    label: t('fleet.fuelType'),
    sortable: true,
    sortKey: 'fuel_type',
    filterable: true,
    render: (v) => (v ? formatFuelTypeLabel(String(v)) : <span className="text-textTertiary">-</span>),
  },
  {
    key: 'reference_number',
    label: t('fleet.referenceNumber'),
    sortable: true,
    sortKey: 'reference_number',
    filterable: true,
    align: 'left',
    width: 140,
    render: (_, row) => (
      <FleetVehicleReferenceInput
        vehicle={row}
        suggestions={referenceNames[String(row.organization_id)] ?? []}
        onSave={handleSaveVehicleReference}
        onRemembered={(name) => rememberReference(String(row.organization_id), name)}
      />
    ),
    csvValue: (v) => (v != null && String(v).trim() !== '' ? String(v) : ''),
  },
  {
    key: 'assigned_user_name',
    label: t('fleet.assignedUserColumn'),
    sortable: true,
    sortKey: 'assigned_user_name',
    filterable: true,
    render: (v) => {
      const name = v != null ? String(v).trim() : '';
      return name ? (
        <span className="text-sm text-gray-800">{name}</span>
      ) : (
        <span className="text-textTertiary">-</span>
      );
    },
    csvValue: (v) => (v != null ? String(v).trim() : ''),
  },
  {
    key: 'vehicle_category_name',
    label: t('fleet.vehicleCategoryColumn'),
    sortable: true,
    sortKey: 'vehicle_category_name',
    filterable: true,
    render: (v) => (v ? String(v) : <span className="text-textTertiary">-</span>),
    csvValue: (v) => (v != null && String(v).trim() !== '' ? String(v) : ''),
  },
  {
    key: 'organization_name',
    label: t('fleet.organization'),
    sortable: true,
    sortKey: 'organization_name',
    filterable: true,
    render: (v) => (v ? String(v) : <span className="text-textTertiary">-</span>),
  },
  {
    key: 'ownership_type',
    label: t('fleet.ownershipColumn'),
    sortable: true,
    sortKey: 'ownership_type',
    filterable: true,
    render: (v) => {
      const type = resolveOwnershipType(v != null ? String(v) : null);
      if (!type) return <span className="text-textTertiary">-</span>;
      return <span className="text-sm text-gray-800">{t(ownershipLabelKey(type))}</span>;
    },
    csvValue: (v) => {
      const type = resolveOwnershipType(v != null ? String(v) : null);
      return type ? t(ownershipLabelKey(type)) : '';
    },
  },
  {
    key: 'installed_tire_type',
    label: t('fleet.installedTires') || 'Däck',
    sortable: true,
    sortKey: 'installed_tire_type',
    filterable: true,
    render: (_, row) => {
      const type = row.installed_tire_type as string | null;
      const brand = row.installed_tire_brand as string | null;
      const size = row.installed_tire_size as string | null;
      const tread = row.installed_tire_tread_mm as number | null;
      if (!type) return <span className="text-textTertiary text-xs">{t('service.tires.noTireInfo')}</span>;
      const typeLabel = type === 'SUMMER' ? '☀️' : type === 'WINTER_STUDDED' ? '❄️🔩' : '❄️';
      const treadColor = tread == null ? '' : tread < 3 ? 'text-red-600 font-semibold' : tread < 4 ? 'text-amber-600' : 'text-green-700';
      return (
        <div className="text-xs leading-tight">
          <div className="flex items-center gap-1">
            <span>{typeLabel}</span>
            {brand && <span className="font-medium text-gray-800">{brand}</span>}
          </div>
          {size && <div className="text-textTertiary">{size}</div>}
          {tread != null && <div className={treadColor}>{tread} mm</div>}
        </div>
      );
    },
    csvValue: (_, row) => {
      const parts = [row.installed_tire_type, row.installed_tire_brand, row.installed_tire_size].filter(Boolean);
      const tread = row.installed_tire_tread_mm;
      if (tread) parts.push(`${tread}mm`);
      return parts.join(' ') || '';
    },
  },
  {
    key: '_fuelLevel',
    label: t('fleet.fuelLevel'),
    sortable: true,
    sortKey: '_fuelLevel',
    render: (_, row) => (
      <span title={t('fleet.fuelEvTooltip')}>
        {formatFuelDisplay(row.id, fuelStatus)}
      </span>
    ),
    csvValue: (_, row) => formatFuelDisplay(row.id, fuelStatus),
  },
  {
    key: '_evRange',
    label: t('fleet.evRange'),
    sortable: true,
    sortKey: '_evRange',
    render: (_, row) => formatEvRange(row.id, fuelStatus),
    csvValue: (_, row) => formatEvRange(row.id, fuelStatus),
  },
  {
    key: '_serviceDistance',
    label: t('fleet.serviceDistance'),
    sortable: true,
    sortKey: '_serviceDistance',
    render: (_, row) => formatServiceDistance(row.id, fuelStatus),
    csvValue: (_, row) => formatServiceDistance(row.id, fuelStatus),
  },
  {
    key: 'last_seen',
    label: t('fleet.lastSeen'),
    sortable: true,
    sortKey: 'last_seen',
    filterable: true,
    sortValue: (row) => (row.last_seen ? new Date(row.last_seen).getTime() : 0),
    csvValue: (v) => {
      if (!v) return '';
      const d = new Date(String(v));
      const f = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Stockholm', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
      const parts = f.formatToParts(d);
      const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
      const y = get('year');
      const m = get('month').padStart(2, '0');
      const day = get('day').padStart(2, '0');
      const h = get('hour').padStart(2, '0');
      const min = get('minute').padStart(2, '0');
      const formatted = `${y}-${m}-${day} ${h}:${min}`;
      return `="${formatted}"`;
    },
    render: (_, row) => (
      <div>
        <div className={getLastSeenColor(row.last_seen)}>
          {formatLastSeen(row.last_seen)}
        </div>
        {row.last_seen && (
          <div className="text-xs text-textTertiary mt-1">
            {new Date(row.last_seen).toLocaleString('sv-SE', {
              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
            })}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'device_imei',
    label: t('fleet.imeiColumn') || 'IMEI',
    sortable: true,
    sortKey: 'device_imei',
    filterable: true,
    csvValue: (v) => (v ? `="${String(v)}"` : ''),
    render: (v, row) => {
      if (!v) return <span className="text-textTertiary">-</span>;
      const fw = (row.device_firmware ?? '').toString().trim();
      const config = (row.device_configuration ?? '').toString().trim();
      const unknown = t('fleet.imeiUnknown') || 'okänd';
      const tooltip = `${t('fleet.imeiFirmware') || 'Firmware'}: ${fw || unknown}\n${t('fleet.imeiConfig') || 'Konfiguration'}: ${config || unknown}`;
      return (
        <span
          className="font-mono text-textTertiary text-sm cursor-help"
          title={tooltip}
        >
          {String(v)}
        </span>
      );
    },
  },
  {
    key: 'placement_city',
    label: t('fleet.placementCity'),
    sortable: true,
    sortKey: 'placement_city',
    filterable: true,
    render: (v) => (v ? String(v) : <span className="text-textTertiary">-</span>),
    csvValue: (v) => (v != null && String(v).trim() !== '' ? String(v) : ''),
  },
  {
    key: 'current_odometer',
    label: t('fleet.currentOdometer'),
    sortable: true,
    sortKey: 'current_odometer',
    filterable: true,
    sortValue: (row) => Number(row.current_odometer) || 0,
    render: (_, row) => {
      const text = formatCurrentOdometerKm(row.current_odometer);
      return text ? (
        <span>{text}</span>
      ) : (
        <span className="text-textTertiary">-</span>
      );
    },
    csvValue: (_, row) => formatCurrentOdometerKm(row.current_odometer),
  },
  {
    key: 'flinkey_box_id',
    label: t('fleet.flinkeyBoxColumn'),
    sortable: true,
    sortKey: 'flinkey_box_id',
    filterable: true,
    render: (v) =>
      v ? (
        <span className="font-mono text-xs text-gray-700">{String(v)}</span>
      ) : (
        <span className="text-textTertiary">-</span>
      ),
  },
  {
    key: 'transmission',
    label: t('fleet.transmissionColumn'),
    sortable: true,
    sortKey: 'transmission',
    filterable: true,
    render: (v) => (v ? String(v) : <span className="text-textTertiary">-</span>),
  },
  {
    key: 'business_responsibility',
    label: t('fleet.businessResponsibilityColumn'),
    sortable: true,
    sortKey: 'business_responsibility_ansvar_text',
    filterable: true,
    render: (_, row) => {
      const text = row.business_responsibility_ansvar_text;
      const ik = row.business_responsibility_ikpart_text;
      if (!text) return <span className="text-textTertiary">-</span>;
      return (
        <div className="text-xs leading-tight max-w-[200px]">
          <div className="text-gray-800">{String(text)}</div>
          {ik && <div className="text-textTertiary">{String(ik)}</div>}
        </div>
      );
    },
    csvValue: (_, row) => {
      const parts = [row.business_responsibility_ansvar_text, row.business_responsibility_ikpart_text].filter(Boolean);
      return parts.join(' / ');
    },
  },
  {
    key: 'alternative_fuel_name',
    label: t('fleet.alternativeFuelColumn'),
    sortable: true,
    sortKey: 'alternative_fuel_name',
    filterable: true,
    render: (v) => (v ? String(v) : <span className="text-textTertiary">-</span>),
  },
  {
    key: 'status',
    label: t('fleet.vehicleStatusColumn'),
    sortable: true,
    sortKey: 'usage_status_name',
    filterable: true,
    render: (_, row) => {
      const name = row.usage_status_name;
      if (!name) {
        return <span className="text-textTertiary">{t('fleet.noUsageStatus')}</span>;
      }
      return <span className="text-sm font-medium text-textDefault">{String(name)}</span>;
    },
    csvValue: (_, row) => (row.usage_status_name ? String(row.usage_status_name) : t('fleet.noUsageStatus')),
  },
  {
    key: 'pickup_placement_names',
    label: t('fleet.pickupPlacementColumn'),
    sortable: true,
    sortKey: 'pickup_placement_names',
    filterable: true,
    render: (v) => {
      const text = v != null ? String(v).trim() : '';
      if (!text) return <span className="text-textTertiary">-</span>;
      const places = text.split(',').map((p) => p.trim()).filter(Boolean);
      return (
        <div className="text-xs leading-tight max-w-[220px]">
          {places.map((p, i) => (
            <div key={i} className="text-gray-800">{p}</div>
          ))}
        </div>
      );
    },
    csvValue: (v) => (v != null && String(v).trim() !== '' ? String(v) : ''),
  },
  {
    key: '_actions',
    label: t('fleet.functions'),
    align: 'right',
    sortable: false,
    render: (_, vehicle) => (
      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          title={getMilTitle(getMilStatus(vehicle), t)}
          className={`p-1.5 rounded hover:bg-gray-100 ${getMilColor(getMilStatus(vehicle))}`}
        >
          <ExclamationTriangleIcon className="h-5 w-5" />
        </button>
        {(() => {
          const st = String(vehicle.status ?? '')
            .trim()
            .toUpperCase();
          if (st === 'INACTIVE') {
            return (
              <button
                type="button"
                onClick={() => handleActivate(vehicle)}
                title={t('fleet.activateVehicle')}
                className="p-1.5 rounded hover:bg-green-100 text-textTertiary hover:text-green-600"
              >
                <PowerIcon className="h-5 w-5" />
              </button>
            );
          }
          if (st === 'RETIRED') {
            return null;
          }
          return (
            <button
              type="button"
              onClick={() => handleDeactivate(vehicle)}
              title={t('fleet.deactivate')}
              className="p-1.5 rounded hover:bg-red-100 text-textTertiary hover:text-red-600"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          );
        })()}
        <button
          type="button"
          onClick={() => handleConnectDevice(vehicle)}
          title={t('fleet.connectDevice')}
          className={`p-1.5 rounded hover:bg-blue-100 ${vehicle.device_imei ? 'text-green-500' : 'text-textTertiary'} hover:text-blue-600`}
        >
          <DevicePhoneMobileIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => handleConnectKey(vehicle)}
          title={t('fleet.connectKey')}
          className="p-1.5 rounded hover:bg-yellow-100 text-textTertiary hover:text-yellow-600"
        >
          <KeyIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => handleSubscribe(vehicle)}
          title={t('fleet.subscribe')}
          className="p-1.5 rounded hover:bg-purple-100 text-textTertiary hover:text-purple-600"
        >
          <BellIcon className="h-5 w-5" />
        </button>
      </div>
    ),
  },
];

function FleetDataTable({
  data,
  t,
  fuelStatus,
  setSelectedVehicle,
  handleDeactivate,
  handleActivate,
  handleConnectDevice,
  handleConnectKey,
  handleSubscribe,
  handleSaveVehicleReference,
  emptyMessage,
  columnPreferences,
  manualSort,
  columnFiltersEnabled,
  manualColumnFilters,
  fillHeight = false,
  referenceNames,
  rememberReference,
}: {
  data: (Vehicle & { organization_name?: string; last_seen?: string })[];
  t: (key: string) => string;
  fuelStatus: Record<string, FuelStatus>;
  setSelectedVehicle: (sel: FleetVehicleCardSelection) => void;
  handleDeactivate: (v: Vehicle) => void;
  handleActivate: (v: Vehicle) => void;
  handleConnectDevice: (v: Vehicle) => void;
  handleConnectKey: (v: Vehicle) => void;
  handleSubscribe: (v: Vehicle) => void;
  handleSaveVehicleReference: (v: Vehicle, raw: string) => Promise<void>;
  emptyMessage: string;
  columnPreferences: FleetOverviewColumnPreferences;
  manualSort: {
    sortBy: string | null;
    sortDir: 'asc' | 'desc';
    onSortChange: (sortBy: string, sortDir: 'asc' | 'desc') => void;
  };
  columnFiltersEnabled: boolean;
  manualColumnFilters: {
    filters: Record<string, string>;
    onFilterChange: (key: string, value: string) => void;
  };
  fillHeight?: boolean;
  referenceNames: Record<string, string[]>;
  rememberReference: (organizationId: string, name: string) => void;
}) {
  const columns = useMemo(() => {
    const all = fleetColumns(
      t,
      fuelStatus,
      setSelectedVehicle,
      handleDeactivate,
      handleActivate,
      handleConnectDevice,
      handleConnectKey,
      handleSubscribe,
      handleSaveVehicleReference,
      referenceNames,
      rememberReference
    );
    return applyFleetColumnOrder(all, columnPreferences);
  }, [
    t,
    fuelStatus,
    setSelectedVehicle,
    handleDeactivate,
    handleActivate,
    handleConnectDevice,
    handleConnectKey,
    handleSubscribe,
    handleSaveVehicleReference,
    columnPreferences,
    referenceNames,
    rememberReference,
  ]);
  return (
    <DataTable<Vehicle & { organization_name?: string; last_seen?: string }>
      columns={columns}
      data={data}
      rowKey="id"
      pageSize={0}
      emptyMessage={emptyMessage}
      features={{ csvExport: false, horizontalScroll: true, columnFilters: columnFiltersEnabled }}
      manualSort={manualSort}
      manualColumnFilters={manualColumnFilters}
      fillHeight={fillHeight}
    />
  );
}

const FleetOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const tourRef = useRef<ModuleGuideTourHandle>(null);

  const isAdminPreview = (location.state as any)?.startGuide === 'module_fleet';

  // Starta turen automatiskt om admin navigerat hit via guide-preview
  useEffect(() => {
    if (isAdminPreview) {
      const timer = setTimeout(() => tourRef.current?.startTour(), 600);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const { permissions } = usePermissions();
  const {
    filteredVehicles,
    isLoading,
    error,
    pagination,
    search,
    searchInput,
    setSearchInput,
    selectedVehicle,
    setSelectedVehicle,
    showAddVehicleModal,
    setShowAddVehicleModal,
    showCarInfoFilterModal,
    setShowCarInfoFilterModal,
    carInfoFilterIds,
    applyCarInfoFilter,
    clearCarInfoFilter,
    fuelStatus,
    statusFilter,
    setStatusFilter,
    connectedFilter,
    setConnectedFilter,
    organizationFilter,
    presetFilter,
    setPresetFilter,
    usageStatusFilter,
    setUsageStatusFilter,
    usageStatusOptions,
    sortBy,
    sortDir,
    columnFilters,
    columnFiltersEnabled,
    loadVehicles,
    handleSearch,
    clearAllFilters,
    handleSortChange,
    handleColumnFilterChange,
    toggleColumnFilters,
    handleOrganizationFilterChange,
    goToPage,
    handleDeactivate,
    handleActivate,
    handleConnectDevice,
    handleConnectKey,
    handleSubscribe,
    handleSaveVehicleReference,
    deviceLinkVehicle,
    setDeviceLinkVehicle,
  } = useFleetOverview(t);

  const [referenceNames, setReferenceNames] = React.useState<Record<string, string[]>>({});

  React.useEffect(() => {
    const ids = [
      ...new Set(
        filteredVehicles
          .map((vehicle) => String(vehicle.organization_id ?? ''))
          .filter((id) => /^\d+$/.test(id))
      ),
    ];
    if (ids.length === 0) return;
    let cancelled = false;
    vehiclesService.getReferenceValues(ids).then((map) => {
      if (!cancelled) {
        setReferenceNames((prev) => ({ ...prev, ...map }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [filteredVehicles]);

  const rememberReference = React.useCallback((organizationId: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed || !organizationId) return;
    setReferenceNames((prev) => {
      const current = prev[organizationId] ?? [];
      if (current.some((item) => item.localeCompare(trimmed, 'sv', { sensitivity: 'accent' }) === 0)) {
        return prev;
      }
      return {
        ...prev,
        [organizationId]: [...current, trimmed].sort((a, b) => a.localeCompare(b, 'sv')),
      };
    });
  }, []);

  const [exportRegisterLoading, setExportRegisterLoading] = React.useState(false);
  const [columnSettingsOpen, setColumnSettingsOpen] = React.useState(false);

  // Bevara scroll-position i listan när man öppnar/stänger ett fordonskort.
  const pageRootRef = useRef<HTMLDivElement>(null);

  // Fyll den synliga ytan under headern så tabellen scrollar i sig själv.
  // Procenthöjd räcker inte alltid när layoutens scroll-yta växer med innehållet.
  useLayoutEffect(() => {
    const el = pageRootRef.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;
    const sync = () => {
      el.style.height = `${parent.clientHeight}px`;
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(parent);
    return () => {
      observer.disconnect();
      el.style.height = '';
    };
  }, []);
  const savedScrollRef = useRef(0);
  const pendingScrollRestoreRef = useRef(false);

  /** Listans egen rullist (tabellen), annars närmaste scrollbara förälder. */
  const getScrollContainer = useCallback((): HTMLElement | null => {
    const tableScroll = pageRootRef.current?.querySelector<HTMLElement>('[data-dq-table-scroll]');
    if (tableScroll) return tableScroll;
    let el = pageRootRef.current?.parentElement ?? null;
    while (el) {
      const style = window.getComputedStyle(el);
      if (/(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }, []);

  /** Öppna fordonskort och kom ihåg var i listan vi var. */
  const handleOpenVehicle = useCallback(
    (sel: FleetVehicleCardSelection) => {
      const container = getScrollContainer();
      savedScrollRef.current = container ? container.scrollTop : 0;
      setSelectedVehicle(sel);
    },
    [getScrollContainer, setSelectedVehicle]
  );

  // Återställ scroll-positionen när kortet stängts och listan laddats om.
  useLayoutEffect(() => {
    if (!pendingScrollRestoreRef.current) return;
    if (selectedVehicle || isLoading) return;
    const container = getScrollContainer();
    if (container) container.scrollTop = savedScrollRef.current;
    pendingScrollRestoreRef.current = false;
  }, [selectedVehicle, isLoading, filteredVehicles, getScrollContainer]);

  const {
    preferences: fleetColumnPreferences,
    saving: fleetColumnSaving,
    save: saveFleetColumnPreferences,
    resetToDefault: resetFleetColumnPreferences,
  } = useFleetColumnPreferences();

  const handleExportVehicleRegister = async (format: 'xlsx' | 'csv') => {
    setExportRegisterLoading(true);
    try {
      await downloadVehicleRegisterExport({
        format,
        organizationId: organizationFilter || undefined,
      });
      toast.success(t('fleet.exportVehicleRegisterSuccess'));
    } catch (e: any) {
      toast.error(e?.message || t('fleet.exportVehicleRegisterError'));
    } finally {
      setExportRegisterLoading(false);
    }
  };

  const handleExportFleetReport = async (format: 'xlsx' | 'csv') => {
    setExportRegisterLoading(true);
    try {
      const exportVehicles = await fetchAllFilteredFleetVehicles({
        search,
        statusFilter,
        organizationFilter,
        carInfoFilterIds,
        connectedFilter,
        presetFilter,
        usageStatusId: usageStatusFilter,
      });

      if (exportVehicles.length === 0) {
        toast.error(t('fleet.noVehicles'));
        return;
      }

      let exportFuelStatus = { ...fuelStatus };
      const missingFuelIds = exportVehicles
        .map((v) => v.id)
        .filter((id) => exportFuelStatus[id] === undefined);
      if (missingFuelIds.length > 0) {
        exportFuelStatus = {
          ...exportFuelStatus,
          ...(await vehiclesService.getVehicleFuelStatus(missingFuelIds)),
        };
      }

      const allColumns = fleetColumns(
        t,
        exportFuelStatus,
        setSelectedVehicle,
        handleDeactivate,
        handleActivate,
        handleConnectDevice,
        handleConnectKey,
        handleSubscribe,
        handleSaveVehicleReference,
        referenceNames,
        rememberReference
      );
      const orderedColumns = applyFleetColumnOrder(allColumns, fleetColumnPreferences);

      await downloadFleetGridReportExport({
        format,
        columns: orderedColumns,
        data: exportVehicles,
      });
      toast.success(t('fleet.exportFleetReportSuccess'));
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t('fleet.exportFleetReportError');
      toast.error(message);
    } finally {
      setExportRegisterLoading(false);
    }
  };

  const canAddVehicle = useMemo(() => {
    if (user?.is_global_admin) {
      return true;
    }

    const permissionList = permissions?.permissions ?? [];
    if (permissionList.length === 0) return false;

    // Strict deny: kräver explicit action (ingen null-action fallback)
    return permissionList.some((permission) => {
      const featureCode = permission.feature?.code ?? '';
      const actionCode = permission.action?.code ?? '';
      const matchesFeature = featureCode === 'FLEET';
      const matchesAction = actionCode === 'ADD' || actionCode === 'CREATE_VEHICLE';
      return matchesFeature && matchesAction;
    });
  }, [permissions, user?.is_global_admin]);

  return (
    <div ref={pageRootRef} className="flex h-full min-h-0 min-w-0 max-w-full flex-col overflow-hidden px-4 pt-6 sm:px-0">
      <ModuleGuideTour
        ref={tourRef}
        tourKey="module_fleet"
        autoStart={false}
        onFinish={isAdminPreview ? () => navigate('/admin/guide-management') : undefined}
      />

      <div className="shrink-0">
      <FleetOverviewHeader
        t={t}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        search={search}
        onSearch={handleSearch}
        onShowGuide={() => tourRef.current?.startTour()}
        canAddVehicle={canAddVehicle}
        onAddVehicle={() => setShowAddVehicleModal(true)}
        onOpenColumnSettings={() => setColumnSettingsOpen(true)}
        onOpenCarInfoFilter={() => setShowCarInfoFilterModal(true)}
        carInfoFilterIds={carInfoFilterIds}
        exportRegisterLoading={exportRegisterLoading}
        onExportVehicleRegister={handleExportVehicleRegister}
        onExportFleetReport={handleExportFleetReport}
        organizationFilter={organizationFilter}
        onOrganizationFilterChange={handleOrganizationFilterChange}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        connectedFilter={connectedFilter}
        setConnectedFilter={setConnectedFilter}
        presetFilter={presetFilter}
        setPresetFilter={setPresetFilter}
        usageStatusFilter={usageStatusFilter}
        setUsageStatusFilter={setUsageStatusFilter}
        usageStatusOptions={usageStatusOptions}
        onClearFilters={clearAllFilters}
        vehicleCount={!isLoading && !error ? pagination.total : null}
        columnFiltersEnabled={columnFiltersEnabled}
        onToggleColumnFilters={toggleColumnFilters}
      />
      </div>

      {error ? (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-red-500">{t('fleet.error') || 'Kunde inte ladda fordon'}</p>
        </div>
      ) : isLoading && filteredVehicles.length === 0 && !columnFiltersEnabled ? (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-textTertiary">{t('fleet.loading')}</p>
        </div>
      ) : filteredVehicles.length === 0 && !columnFiltersEnabled ? (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-textTertiary">{t('fleet.noVehicles')}</p>
          {organizationFilter && (
            <p className="text-sm text-textTertiary mt-2">
              {t('fleet.tryAllOrganizations') || 'Prova att välja "Alla organisationer" i filtret.'}
            </p>
          )}
        </div>
      ) : (
        // Behåll tabellen (och filterfälten) monterad under omladdning så att fokus
        // i en filterkolumn inte försvinner medan man skriver. Vid kolumnfilter-reload
        // visas en diskret indikator i stället för att ersätta hela tabellen.
        <div data-guide="fleet-table" className="relative flex min-h-0 min-w-0 max-w-full flex-1 flex-col">
          {isLoading && (
            <div className="absolute right-2 top-2 z-20 pointer-events-none rounded bg-white/90 px-2 py-1 text-xs text-textTertiary shadow-sm">
              {t('fleet.loading')}
            </div>
          )}
          <div className="min-h-0 flex-1">
          <FleetDataTable
            data={filteredVehicles}
            t={t}
            fuelStatus={fuelStatus}
            setSelectedVehicle={handleOpenVehicle}
            handleDeactivate={handleDeactivate}
            handleActivate={handleActivate}
            handleConnectDevice={handleConnectDevice}
            handleConnectKey={handleConnectKey}
            handleSubscribe={handleSubscribe}
            handleSaveVehicleReference={handleSaveVehicleReference}
            emptyMessage={t('fleet.noVehicles')}
            columnPreferences={fleetColumnPreferences}
            manualSort={{ sortBy, sortDir, onSortChange: handleSortChange }}
            columnFiltersEnabled={columnFiltersEnabled}
            manualColumnFilters={{ filters: columnFilters, onFilterChange: handleColumnFilterChange }}
            fillHeight
            referenceNames={referenceNames}
            rememberReference={rememberReference}
          />
          </div>
          {pagination.totalPages > 1 && (
            <div className="shrink-0 px-6 py-4 bg-bgLight border border-t-0 border-border rounded-b-lg flex items-center justify-between">
              <div className="text-sm text-text-muted">
                {t('fleet.page') || 'Sida'} {pagination.page} {t('fleet.of') || 'av'} {pagination.totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => goToPage(1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 rounded border border-border bg-bgContainer text-text-default hover:bg-bgHover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('fleet.first') || 'Första'}
                </button>
                <button
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="p-2 rounded border border-border bg-bgContainer text-text-default hover:bg-bgHover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) pageNum = i + 1;
                    else if (pagination.page <= 3) pageNum = i + 1;
                    else if (pagination.page >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                    else pageNum = pagination.page - 2 + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`px-3 py-1 rounded border ${
                          pageNum === pagination.page
                            ? 'bg-primary text-white border-primary'
                            : 'border-border bg-bgContainer text-text-default hover:bg-bgHover'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="p-2 rounded border border-border bg-bgContainer text-text-default hover:bg-bgHover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => goToPage(pagination.totalPages)}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-3 py-1 rounded border border-border bg-bgContainer text-text-default hover:bg-bgHover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('fleet.last') || 'Sista'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vehicle Card Modal */}
      {selectedVehicle && (
        <VehicleCard
          registrationNumber={selectedVehicle.registrationNumber}
          vehicleId={selectedVehicle.vehicleId}
          onClose={() => {
            pendingScrollRestoreRef.current = true;
            setSelectedVehicle(null);
            loadVehicles(pagination.page, search, statusFilter, organizationFilter);
          }}
        />
      )}

      {/* Add Vehicle Modal */}
      <AddVehicleModal
        isOpen={showAddVehicleModal}
        onClose={() => setShowAddVehicleModal(false)}
        onVehicleCreated={() => loadVehicles(1, search, statusFilter, organizationFilter)}
      />
      
      <CarInfoFilterModal
        isOpen={showCarInfoFilterModal}
        onClose={() => setShowCarInfoFilterModal(false)}
        onSearch={applyCarInfoFilter}
        onClearApplied={clearCarInfoFilter}
      />

      {/* Koppla enhet till fordon */}
      {deviceLinkVehicle && (
        <VehicleDeviceLinkModal
          vehicle={deviceLinkVehicle}
          onClose={() => setDeviceLinkVehicle(null)}
          onSuccess={() => {
            setDeviceLinkVehicle(null);
            loadVehicles(1, search, statusFilter, organizationFilter);
          }}
        />
      )}

      <FleetColumnSettingsModal
        isOpen={columnSettingsOpen}
        onClose={() => setColumnSettingsOpen(false)}
        preferences={fleetColumnPreferences}
        saving={fleetColumnSaving}
        onSave={async (prefs) => {
          try {
            await saveFleetColumnPreferences(prefs);
            toast.success(t('fleet.columnSettings.saveSuccess'));
          } catch (e: unknown) {
            toast.error(e instanceof Error ? e.message : t('fleet.columnSettings.saveError'));
          }
        }}
        onReset={async () => {
          try {
            await resetFleetColumnPreferences();
            toast.success(t('fleet.columnSettings.resetSuccess'));
          } catch (e: unknown) {
            toast.error(e instanceof Error ? e.message : t('fleet.columnSettings.saveError'));
          }
        }}
      />
    </div>
  );
};

export default FleetOverview;
